
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Trash2, UserX, Calendar } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { Input } from "./ui/input";

import { MeasureData } from "../../pt/utils/interfaces";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { formatDate } from "../../pt/utils/date-utils";
import { useState } from "react";
import { Label } from "./ui/label";
import { ClientDataHolder } from "../../pt/dashboard/data/data";
import { FeedbackDialog } from "./FeedbackDialog";
import { on } from "events";



interface EditModeProps {
  measurements: MeasureData[];
  gender: "male" | "female";
  onConfirmMeasurement: (measurement: MeasureData) => void;
  onDeleteMeasurement: (ids: string[]) => void;
  onDeleteClient: () => void;
  onClose: () => void;
}

export const EditMode = ({ measurements, gender, onConfirmMeasurement, onDeleteMeasurement, onDeleteClient, onClose }: EditModeProps) => {
  const { toast } = useToast();
  const [deleteConfirmation, setDeleteConfirmation] = useState("");


  // Group measurements by date
  const measurementsByDate = measurements.reduce((acc, measurement, index) => {
    const date = formatDate(new Date(measurement.createdAt))|| "Unknown date";
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push({ ...measurement, originalIndex: index });
    return acc;
  }, {} as Record<string, Array<MeasureData & { originalIndex: number }>>);
 

  const handleDeleteSession = (date: string, id: string) => {
    const measurementsToDelete = measurements.find(m => m.ID === id) ? [measurements.find(m => m.ID === id)!] : [];
    if (measurementsToDelete.length === 0) {
      toast({
        title: "No measurements found",
        description: `No measurements found for the selected session.`,
        variant: "destructive",
      });
      return;
    }
    onDeleteMeasurement(measurementsToDelete.map(m => m.ID));
    // Delete from highest index to lowest to avoid index shifting issues

    toast({
      title: "Measurement session deleted",
      description: `All measurements from ${date} have been removed from the time series.`,
    });
  };

  const handleDeleteClient = () => {
    if (deleteConfirmation.toUpperCase() === "DELETE") {

      onDeleteClient();
      setDeleteConfirmation("");
      toast({
        title: "Client deleted",
        description: "Client and all measurement data have been removed.",
        variant: "destructive",
      });
    }
  };

  const isDeleteEnabled = deleteConfirmation.toUpperCase() === "DELETE";
  return (
    <Card className="p-6 backdrop-blur-lg bg-card/80 border border-red-200/50 animate-fade-in">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-red-600">Edit Mode</h3>
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </div>
        
        <div className="space-y-2 ">
          <h4 className="text-sm font-medium text-gray-700">Delete Measurement Sessions</h4>
          <p className="text-xs text-gray-500 mb-3">Remove all measurements from a specific date/session</p>
          <ScrollArea className="h-64 w-full rounded border overflow-y-scroll">
            <div className="space-y-2 p-2">
              {measurements.map((sessionMeasurements) => {
                const date = formatDate(new Date(sessionMeasurements.createdAt)) || "Unknown date";
                return (
                <div key={sessionMeasurements.ID} className="flex items-center justify-between p-3 border rounded">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium">{date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    
                      <FeedbackDialog
                        gender={gender}
                        measureData={sessionMeasurements}
                        onUpdateMeasurement={onConfirmMeasurement}
                      />
                    
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Measurement Session</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete all measurements from {date}?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleDeleteSession(date, sessionMeasurements.ID)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Delete Session
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                </div>
                
              )})}
              </div>
          </ScrollArea>
        </div>

        <div className="pt-4 border-t">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="w-full">
                <UserX className="w-4 h-4 mr-2" />
                Delete Entire Client
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Client</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this client and all their measurement data? This action cannot be undone and will permanently remove all time series data for this client.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="delete-confirmation">
                    Type "DELETE" to confirm:
                  </Label>
                  <Input
                    id="delete-confirmation"
                    type="text"
                    placeholder="Type DELETE"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                  />
                </div>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setDeleteConfirmation("")}>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDeleteClient}
                  disabled={!isDeleteEnabled}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete Client
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </Card>
  );
};

